import { useState, useEffect, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Avatar,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import InsertEmoticonIcon from "@mui/icons-material/InsertEmoticon";
import { Button, FoodsIcon, FoodsIconButton, FoodsTags, FoodsTooltip, Textarea } from "@foodpilot/foods";
import { useForm, Controller } from "react-hook-form";
import { CreatePostData, SocialPostType } from "../../types/post.types";
import { useCreatePost } from "../../../../api/client-api/social/usePosts";
import {
    SocialUploadedFile,
    toAttachmentMetadata,
    useSocialFileUpload,
} from "../../../../api/client-api/social/useSocialFileUpload";
import { UploadedAttachmentsPreview } from "./UploadedAttachmentsPreview";
import { EmojiPicker } from "./EmojiPicker";
import { useTranslation } from "react-i18next";
import { injectTypeHashtag } from "../../utils/postType";
import { ExpandedPostTypeSelector } from "../Prompts/SocialPublishActions";
import { resolveSocialComposerPlaceholder } from "../../utils/socialComposerPlaceholder";
import { useSnackbar } from "src/utils/useSnackbar";

const MAX_POST_LENGTH = 2000;

interface ExpandedCreatePostProps {
    open: boolean;
    onClose: () => void;
    user: any;
    onSuccess?: () => void;
    initialType?: SocialPostType;
    initialContent?: string;
    openImageUpload?: boolean;
    openDocumentUpload?: boolean;
}

const getTranslatedVisibilityLabel = (
    visibility: CreatePostData["visibility"],
    t: (key: string, defaultValue: string) => string,
) => {
    switch (visibility) {
        case "private":
            return t("social.create_post.visibility.private", "Private");
        case "company":
            return t("social.create_post.visibility.company", "Company");
        case "public":
        default:
            return t("social.create_post.visibility.public", "Community");
    }
};

export const ExpandedCreatePost = ({
    open,
    onClose,
    user,
    onSuccess,
    initialType = "generic",
    initialContent = "",
    openImageUpload = false,
    openDocumentUpload = false,
}: ExpandedCreatePostProps) => {
    const theme = useTheme();
    const createPost = useCreatePost();
    const { uploadFiles, uploading } = useSocialFileUpload();
    const [uploadedFiles, setUploadedFiles] = useState<SocialUploadedFile[]>([]);
    const [detectedUrls, setDetectedUrls] = useState<string[]>([]);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const imageInputRef = useRef<HTMLInputElement>(null);
    const documentInputRef = useRef<HTMLInputElement>(null);
    const { t } = useTranslation();
    const { setSnackbarProps } = useSnackbar();
    const composerPlaceholder = resolveSocialComposerPlaceholder(t);
    const { control, handleSubmit, watch, reset, setValue, getValues } = useForm<CreatePostData>({
        defaultValues: {
            content: "",
            type: "text",
            socialPostType: initialType,
            visibility: "public",
            attachments: [],
        },
    });

    const content = watch("content");
    const socialPostType = watch("socialPostType") ?? "generic";

    useEffect(() => {
        if (open) {
            setValue("socialPostType", initialType);
            setValue("content", initialContent);
        }
    }, [initialContent, initialType, open, setValue]);

    useEffect(() => {
        if (!open || !openImageUpload) return;
        window.setTimeout(() => imageInputRef.current?.click(), 0);
    }, [open, openImageUpload]);

    useEffect(() => {
        if (!open || !openDocumentUpload) return;
        window.setTimeout(() => documentInputRef.current?.click(), 0);
    }, [open, openDocumentUpload]);

    useEffect(() => {
        if (content) {
            const urlRegex = /(https?:\/\/[^\s]+)/g;
            const urls = content.match(urlRegex) || [];
            setDetectedUrls(urls);
        } else {
            setDetectedUrls([]);
        }
    }, [content]);

    const onSubmit = async (data: CreatePostData) => {
        try {
            const { socialPostType, ...dataWithoutTopics } = data;
            const contentWithType = injectTypeHashtag(data.content, socialPostType ?? "generic");
            const attachmentMetadata = toAttachmentMetadata(uploadedFiles);

            await createPost.mutateAsync({
                ...dataWithoutTopics,
                content: contentWithType,
                metadata: {
                    ...attachmentMetadata,
                    links: detectedUrls,
                },
            });

            reset();
            setUploadedFiles([]);
            setDetectedUrls([]);
            onClose();
            onSuccess?.();
        } catch (error) {
            // Error handled by mutation
        }
    };

    const handleFilesUploaded = (files: SocialUploadedFile[]) => {
        setUploadedFiles((prev) => [...prev, ...files]);
    };

    const handleFileRemoved = (fileId: string) => {
        setUploadedFiles((prev) => prev.filter((file) => file.id !== fileId));
    };

    const handleClose = () => {
        reset();
        setUploadedFiles([]);
        setDetectedUrls([]);
        setShowEmojiPicker(false);
        onClose();
    };

    const handleEmojiSelect = (emoji: string) => {
        const currentContent = getValues("content") || "";
        setValue("content", currentContent + emoji);
    };

    const handleSelectedFiles = async (files: File[]) => {
        if (files.length === 0) return;

        try {
            const uploaded = await uploadFiles(files);
            handleFilesUploaded(uploaded);
        } catch (error) {
            setSnackbarProps({
                type: "error",
                message: t("social.create_post.upload_failed", "Failed to upload file. Please try again."),
            });
        }
    };

    const isSubmitDisabled = !watch("content")?.trim() || createPost.isLoading || uploading;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            disableEnforceFocus
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    maxHeight: "80vh",
                    minHeight: theme.spacing(62.5),
                },
            }}
        >
            <DialogTitle
                sx={{
                    textAlign: "center",
                    py: 2,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    position: "relative",
                }}
            >
                <Typography component="span" variant="h6">
                    {t("social.create_post.create_post", "Create Post")}
                </Typography>
                <Button
                    onClick={handleClose}
                    variant="text"
                    sx={{ position: "absolute", right: theme.spacing(1), top: theme.spacing(1) }}
                >
                    <FoodsIcon icon="close" size={2} />
                </Button>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: "none" }}
                    onChange={(event) => {
                        void handleSelectedFiles(Array.from(event.target.files ?? []));
                        event.currentTarget.value = "";
                    }}
                />
                <input
                    ref={documentInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    multiple
                    style={{ display: "none" }}
                    onChange={(event) => {
                        void handleSelectedFiles(Array.from(event.target.files ?? []));
                        event.currentTarget.value = "";
                    }}
                />

                <Stack direction="row" alignItems="center" gap={2} mb={2}>
                    <Avatar
                        sx={{
                            width: theme.spacing(5),
                            height: theme.spacing(5),
                            bgcolor: theme.palette.primary.main,
                        }}
                    >
                        {user?.firstname?.[0]}
                        {user?.lastname?.[0]}
                    </Avatar>
                    <Box>
                        <Typography variant="subtitle1">
                            {user?.firstname} {user?.lastname}
                        </Typography>
                        <Controller
                            name="visibility"
                            control={control}
                            render={({ field }) => (
                                <FoodsTags
                                    startIcon={field.value === "private" ? "lock" : "organization"}
                                    label={getTranslatedVisibilityLabel(field.value, t)}
                                    onClick={() => {
                                        const options: CreatePostData["visibility"][] = [
                                            "company",
                                            "public",
                                            "private",
                                        ];
                                        const currentIndex = options.indexOf(field.value);
                                        const nextIndex = (currentIndex + 1) % options.length;
                                        field.onChange(options[nextIndex]);
                                    }}
                                />
                            )}
                        />
                    </Box>
                </Stack>

                <Box sx={{ mb: 2 }}>
                    <ExpandedPostTypeSelector
                        value={socialPostType}
                        onChange={(value) => setValue("socialPostType", value as SocialPostType)}
                    />
                </Box>

                <Controller
                    name="content"
                    control={control}
                    rules={{ required: t("social.create_post.content_required", "Content is required") }}
                    render={({ field, fieldState }) => (
                        <Textarea
                            value={field.value}
                            onChange={field.onChange}
                            placeholder={composerPlaceholder}
                            minRows={4}
                            maxLength={MAX_POST_LENGTH}
                            error={!!fieldState.error}
                            errorMessage={fieldState.error?.message}
                        />
                    )}
                />

                {detectedUrls.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Stack direction="row" alignItems="center" gap={1} mb={1}>
                            <FoodsIcon icon="openNewTab" size={1} />
                            <Typography variant="caption" color="text.secondary">
                                {detectedUrls.length === 1 ?
                                    t("social.create_post.link_detected", "Link detected")
                                :   `${detectedUrls.length} ${t("social.create_post.links_detected", "links detected")}`
                                }
                            </Typography>
                        </Stack>
                        {detectedUrls.slice(0, 3).map((url) => (
                            <FoodsTags
                                key={url}
                                label={url.length > 50 ? url.substring(0, 50) + "..." : url}
                                startIcon="openNewTab"
                            />
                        ))}
                    </Box>
                )}

                <UploadedAttachmentsPreview
                    files={uploadedFiles}
                    onRemove={handleFileRemoved}
                    disabled={createPost.isLoading || uploading}
                />
            </DialogContent>

            <DialogActions
                sx={{
                    justifyContent: "space-between",
                    px: 3,
                    py: 2,
                    backgroundColor: theme.palette.grey[50],
                }}
            >
                <Box display="flex" gap={0.5} sx={{ position: "relative" }}>
                    <FoodsIconButton
                        size="s"
                        icon="gallery"
                        tooltip={{ text: t("social.create_post.toolbar.photo", "Photo") }}
                        buttonProps={{
                            type: "button",
                            onClick: () => imageInputRef.current?.click(),
                            disabled: createPost.isLoading || uploading,
                        }}
                    />
                    <FoodsIconButton
                        size="s"
                        icon="documentSpace"
                        tooltip={{ text: t("social.create_post.toolbar.document", "Document") }}
                        buttonProps={{
                            type: "button",
                            onClick: () => documentInputRef.current?.click(),
                            disabled: createPost.isLoading || uploading,
                        }}
                    />
                    <FoodsTooltip title={t("social.create_post.toolbar.emoji", "Emoji")}>
                        <span>
                            <Button
                                variant="icon-s-nobackground"
                                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                disabled={createPost.isLoading || uploading}
                                aria-label={t("social.create_post.toolbar.emoji", "Emoji")}
                            >
                                <InsertEmoticonIcon sx={{ fontSize: 20 }} />
                            </Button>
                        </span>
                    </FoodsTooltip>
                    {showEmojiPicker && (
                        <EmojiPicker onEmojiSelect={handleEmojiSelect} onClose={() => setShowEmojiPicker(false)} />
                    )}
                </Box>

                <Button
                    onClick={handleSubmit(onSubmit)}
                    variant="primary"
                    disabled={isSubmitDisabled}
                    sx={{ minWidth: 80 }}
                >
                    <Stack direction="row" alignItems="center" gap={1}>
                        <FoodsIcon icon="sendMessage" size={1} />
                        {createPost.isLoading || uploading ?
                            t("social.create_post.posting", "Posting...")
                        :   t("social.create_post.post", "Post")}
                    </Stack>
                </Button>
            </DialogActions>
        </Dialog>
    );
};
